// ============================================================
// ✅ NouvelleDemandeModal.jsx - Version Solde Progressif
// Support pré-remplissage dates depuis le calendrier
// Utilisé UNIQUEMENT pour les demandes normales (plus de mode rétroactif)
// ============================================================

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, Clock, AlertCircle, BookOpen } from 'lucide-react';

export default function NouvelleDemandeModal({ 
  isOpen, onClose, onSuccess, 
  prefillDateDebut, prefillDateFin
}) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typesConge, setTypesConge] = useState([]);
  const [regles, setRegles] = useState(null);
  const [calcul, setCalcul] = useState(null);
  const [nombreJours, setNombreJours] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    ID_Employe: user?.id || '',
    ID_TypeConge: '',
    DateDebut: '',
    DateFin: '',
    EstDemiJourneeDebut: false,
    EstDemiJourneeFin: false,
    Commentaire: ''
  });

  // ✅ Date minimum = demain (J+1)
  const demain = new Date();
  demain.setDate(demain.getDate() + 1);
  const minDate = demain.toISOString().split('T')[0];

  // ==========================================================
  // 🔄 CHARGEMENT
  // ==========================================================
  useEffect(() => {
    if (isOpen) { 
      fetchTypesConge(); 
      resetForm(); 
      setSubmitted(false);
    }
  }, [isOpen, prefillDateDebut, prefillDateFin]);

  const fetchTypesConge = async () => {
    try { const res = await api.get('/typesconge'); setTypesConge(res.data.typesconge || []); }
    catch (err) { /* Silencieux */ }
  };

  const fetchRegles = async (typeId) => {
    if (!typeId) { setRegles(null); setCalcul(null); return; }
    try {
      const res = await api.get(`/demandes/regles/${user.id}/${typeId}`);
      if (res.data.success) {
        setRegles({ infos: res.data.infos, regles: res.data.regles });
        setCalcul(res.data.calcul);
      }
    } catch (err) { /* Silencieux */ }
  };

  const resetForm = () => {
    setFormData({
      ID_Employe: user?.id || '',
      ID_TypeConge: '',
      DateDebut: prefillDateDebut || '',
      DateFin: prefillDateFin || prefillDateDebut || '',
      EstDemiJourneeDebut: false,
      EstDemiJourneeFin: false,
      Commentaire: ''
    });
    setRegles(null);
    setCalcul(null);
    setNombreJours(0);
    setError('');
  };

  const handleTypeChange = (typeId) => {
    setFormData({ ...formData, ID_TypeConge: typeId });
    fetchRegles(typeId);
  };

  // ==========================================================
  // 📅 Calcul jours ouvrés
  // ==========================================================
  useEffect(() => {
    if (formData.DateDebut && formData.DateFin && new Date(formData.DateFin) >= new Date(formData.DateDebut)) {
      fetchJoursOuvrables();
    } else {
      setNombreJours(0);
    }
  }, [formData.DateDebut, formData.DateFin, formData.EstDemiJourneeDebut, formData.EstDemiJourneeFin]);

  const fetchJoursOuvrables = async () => {
    try {
      const res = await api.post('/demandes/calculer-jours', {
        DateDebut: formData.DateDebut,
        DateFin: formData.DateFin,
        ID_Pays: user?.PaysID || 1
      });
      if (res.data.success) {
        let jours = res.data.joursOuvrables;
        if (formData.EstDemiJourneeDebut) jours -= 0.5;
        if (formData.EstDemiJourneeFin) jours -= 0.5;
        setNombreJours(jours > 0 ? jours : 0);
      }
    } catch (err) {
      const debut = new Date(formData.DateDebut);
      const fin = new Date(formData.DateFin);
      let jours = Math.ceil((fin - debut) / (1000 * 60 * 60 * 24)) + 1;
      if (formData.EstDemiJourneeDebut) jours -= 0.5;
      if (formData.EstDemiJourneeFin) jours -= 0.5;
      setNombreJours(jours > 0 ? jours : 0);
    }
  };

  // ==========================================================
  // 💾 SOUMISSION
  // ==========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitted || loading) return;
    
    setError('');

    if (!formData.ID_TypeConge || !formData.DateDebut || !formData.DateFin) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    if (formData.DateDebut < minDate) {
      setError('La date de début doit être à partir de demain (J+1).');
      return;
    }

    if (formData.DateFin < formData.DateDebut) {
      setError('La date de fin doit être après la date de début.');
      return;
    }
    if (nombreJours <= 0) {
      setError('Dates incorrectes.');
      return;
    }

    if (calcul && nombreJours > calcul.JoursRestants) {
      setError(`Solde insuffisant : ${nombreJours} jours demandés, ${calcul.JoursRestants} disponibles.`);
      return;
    }

    setSubmitted(true);
    setLoading(true);
    
    try {
      const res = await api.post('/demandes', formData);
      
      if (res.data.success) { 
        onSuccess();
        onClose();
        setSubmitted(false);
        setLoading(false);
      }
      else { 
        setError(res.data.message); 
        setSubmitted(false);
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création.');
      setSubmitted(false);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up">

        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b px-5 py-4 flex justify-between items-center z-10">
          <h3 className="text-lg font-semibold text-emerald-600">➕ Nouvelle demande</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>

        {/* Erreur */}
        {error && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2 animate-fade-in">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto p-1 text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-5 space-y-4">

          {/* Type de congé */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de congé *</label>
            <select className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 bg-white"
              value={formData.ID_TypeConge} onChange={(e) => handleTypeChange(e.target.value)} required>
              <option value="">Choisir un type...</option>
              {typesConge.map(t => <option key={t.ID_TypeConge} value={t.ID_TypeConge}>{t.Code} - {t.Libelle}</option>)}
            </select>
          </div>

          {/* Bloc Règles */}
          {regles && calcul && (
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={16} className="text-emerald-600" />
                <span className="text-sm font-bold text-emerald-700">RÈGLES DE CALCUL</span>
              </div>
              <div className="text-xs text-gray-600 space-y-1 mb-3">
                <p>🗓️ Embauché le : <strong>{regles.infos?.DateEmbaucheFormatee}</strong></p>
                <p>📅 Ancienneté : <strong>{regles.infos?.AncienneteMois} mois complets</strong></p>
                {!regles.infos?.Eligible && <p className="text-red-600 font-medium">⚠️ {regles.infos?.MessageEligibilite}</p>}
              </div>
              <div className="space-y-2 mb-3">
                {regles.regles.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs"><span className="text-emerald-500 mt-0.5">•</span><span className="text-gray-700">{r.DescriptionLoi}</span></div>
                ))}
              </div>
              {regles.infos?.Eligible && calcul.SoldeProgressif !== undefined && (
                <div className="bg-white rounded-lg p-3 text-xs space-y-1">
                  <p className="font-medium text-gray-700">📐 Détail progressif :</p>
                  <p className="text-gray-600">• {calcul.MoisEcoules} mois écoulés × {Math.round(calcul.TauxProgressif * 100) / 100} j/mois = <strong>{Math.round(calcul.SoldeProgressif * 10) / 10} j</strong></p>
                  {calcul.SoldeDemarrage > 0 && <p className="text-amber-600">• Report N-1 : <strong>+{calcul.SoldeDemarrage} j</strong></p>}
                  {calcul.JoursDejaPris > 0 && <p className="text-red-500">• Déjà pris : <strong>-{calcul.JoursDejaPris} j</strong></p>}
                  <p className="text-emerald-700 font-bold text-sm mt-1">
                    💚 Disponible : {calcul.JoursRestants} j 
                    <span className="text-[10px] text-gray-400 ml-1">
                      (sur {Math.round(calcul.SoldeAnnuel * 10) / 10} j annuels)
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début *</label>
              <input type="date" className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500"
                value={formData.DateDebut} onChange={(e) => setFormData({ ...formData, DateDebut: e.target.value })} 
                min={minDate} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin *</label>
              <input type="date" className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500"
                value={formData.DateFin} onChange={(e) => setFormData({ ...formData, DateFin: e.target.value })} 
                min={formData.DateDebut || minDate} required />
            </div>
          </div>

          {/* Demi-journées */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.EstDemiJourneeDebut} onChange={(e) => setFormData({ ...formData, EstDemiJourneeDebut: e.target.checked })} className="w-4 h-4 text-emerald-500 rounded" />
              <span className="text-xs">½ journée début</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.EstDemiJourneeFin} onChange={(e) => setFormData({ ...formData, EstDemiJourneeFin: e.target.checked })} className="w-4 h-4 text-emerald-500 rounded" />
              <span className="text-xs">½ journée fin</span>
            </label>
          </div>

          {/* Jours calculés */}
          {nombreJours > 0 && (
            <div className={`flex items-center gap-2 p-3 rounded-xl ${calcul && nombreJours > calcul.JoursRestants ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'}`}>
              <Clock size={16} />
              <span className="text-sm font-medium">{nombreJours} jour(s) ouvré(s)</span>
              {calcul && (
                <span className="text-xs ml-auto">Solde après : {Math.max(0, calcul.JoursRestants - nombreJours)} j</span>
              )}
            </div>
          )}

          {/* Commentaire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
            <textarea className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 resize-none" rows={2}
              value={formData.Commentaire} onChange={(e) => setFormData({ ...formData, Commentaire: e.target.value })} />
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-sm font-medium">Annuler</button>
            <button type="submit" disabled={loading || submitted} 
              className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition disabled:opacity-50 text-sm font-medium">
              {loading ? 'Envoi...' : 'Envoyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}